import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { getDrafts, sendDraft, discardDraft, saveDraftText, AiDraft } from '../services/api';

interface Props {
  threadId: number;
  // Bump/change this to force a re-fetch (e.g. when the feed refreshes or the
  // thread's freshest message changes). Mirrors OrderItemEditor's refreshKey.
  refreshKey?: string | number | null;
  onAfterAction: () => void;
  // Reports whether there is currently a draft to show (this component self-hides
  // when empty). Lets the parent gate the "Generated messages" section header +
  // quick-link on real content.
  onHasContent?: (has: boolean) => void;
}

// One pending AI draft: its own editable text, busy + error state, and
// independent Send / Send edited / Discard controls. Lifting this out keeps the
// per-draft state isolated so acting on one draft never disturbs another.
const DraftCard: React.FC<{ draft: AiDraft; onAfterAction: () => void }> = ({ draft, onAfterAction }) => {
  // Seed from the persisted edit (edited_text) if the rep already edited this
  // draft, else the AI's original proposal (draft_text) — so an edit survives
  // close/reopen (D2).
  const [text, setText] = useState((draft.edited_text ?? draft.draft_text) || '');
  // Track WHICH action is in flight (not a single shared flag), so the Send
  // button doesn't read "Sending…" while a Discard is running, and vice-versa.
  const [busyKind, setBusyKind] = useState<null | 'send' | 'discard'>(null);
  const busy = busyKind !== null;
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Last draft_text we saw from the server, so a server-side update to THIS
  // draft (e.g. a regenerated draft arriving via the SignalR-driven refetch)
  // can be adopted — but only while the user hasn't edited the text.
  const lastServerText = useRef(draft.draft_text || '');
  // D2 persistence: savedTextRef = the text last written to the server (seed =
  // the persisted edit or the AI proposal). doneRef mirrors `done` for the
  // unmount-flush's stale closure so a send/discard doesn't trigger a save.
  const savedTextRef = useRef((draft.edited_text ?? draft.draft_text) || '');
  const textLatestRef = useRef(text);
  textLatestRef.current = text;
  const doneRef = useRef(false);

  // Auto-grow the preview to fit the whole draft (Michael's spec: no inner
  // scrollbar for typical drafts). Capped at 60% of the viewport — roughly the
  // modal body's available space — beyond which it scrolls internally.
  const autoSize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    // Skip while the textarea is HIDDEN (e.g. the modal is on the Comments view):
    // scrollHeight is 0 when an ancestor is display:none, which would collapse the
    // box to nothing and leave it clipped when it's shown again. offsetParent is
    // null exactly when hidden. We re-measure on the resize event fired when the
    // Details view is shown.
    if (ta.offsetParent === null) return;
    ta.style.height = 'auto';
    const cap = Math.round(window.innerHeight * 0.6);
    const full = ta.scrollHeight + 2; // +2 = top/bottom border
    ta.style.height = Math.min(full, cap) + 'px';
    ta.style.overflowY = full > cap ? 'auto' : 'hidden';
  }, []);

  // Re-measure whenever the text changes (typing, or an adopted server update)
  // and on mount (a newly generated draft mounts a fresh keyed card).
  useLayoutEffect(() => { autoSize(); }, [text, autoSize]);
  useEffect(() => {
    window.addEventListener('resize', autoSize);
    return () => window.removeEventListener('resize', autoSize);
  }, [autoSize]);

  // If the server's draft_text for this same draft id changes, adopt it only
  // when the user hasn't diverged from the previous server text (their
  // in-progress edits are never clobbered). Adoption updates `text`, which
  // re-triggers autoSize above.
  useEffect(() => {
    const server = draft.draft_text || '';
    if (server !== lastServerText.current) {
      const prevServer = lastServerText.current;
      lastServerText.current = server;
      setText((prev) => (prev.trim() === prevServer.trim() ? server : prev));
    }
  }, [draft.draft_text]);

  // D2: debounced persistence of the in-progress edit, so it survives closing +
  // reopening the thread. Only fires when the text diverges from what's saved.
  useEffect(() => {
    if (doneRef.current || text === savedTextRef.current) return;
    const cur = text;
    const t = setTimeout(() => {
      saveDraftText(draft.id, cur)
        .then(() => { savedTextRef.current = cur; })
        .catch(() => { /* transient — the unmount flush / next debounce retries */ });
    }, 800);
    return () => clearTimeout(t);
  }, [text, draft.id]);

  // Unmount FLUSH: if the modal closes inside the 800ms debounce window, the
  // effect above only clears the timer, so persist the latest edit here. Skipped
  // after a send/discard (doneRef) — the draft is already resolved.
  useEffect(() => () => {
    const cur = textLatestRef.current;
    if (!doneRef.current && cur !== savedTextRef.current) {
      saveDraftText(draft.id, cur).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This card is keyed by draft.id in the parent, so a different draft mounts a
  // fresh instance — the useState initializer above seeds the right text and the
  // user's in-progress edits are never clobbered by a sibling's refetch.
  if (done) return null;

  const act = async (kind: 'send' | 'discard', fn: () => Promise<any>) => {
    setBusyKind(kind);
    setError('');
    try {
      await fn();
      doneRef.current = true;   // resolved — the unmount flush must not re-save
      setDone(true);
      onAfterAction();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Action failed');
    } finally {
      setBusyKind(null);
    }
  };

  const unchanged = text.trim() === (draft.draft_text || '').trim();

  return (
    <div className="rounded-lg border border-brand-300 bg-brand-50/70 px-3 py-2 mb-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-brand-800">
          ✨ Suggested reply — AI draft, review before sending
        </span>
        {draft.confidence != null && (
          <span className="text-xs text-gray-500">· {Math.round(draft.confidence * 100)}% confidence</span>
        )}
      </div>
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        disabled={busy}
        className="w-full text-sm border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60"
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      <div className="flex items-center justify-end gap-2 mt-2">
        <button
          onClick={() => act('discard', () => discardDraft(draft.id))}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-red-600 disabled:opacity-50"
        >
          {busyKind === 'discard' ? 'Discarding…' : 'Discard'}
        </button>
        <button
          onClick={() => act('send', () => sendDraft(draft.id, text))}
          disabled={busy || !text.trim()}
          className="px-3 py-1.5 text-sm font-medium text-white bg-brand-700 rounded-full hover:bg-brand-800 disabled:opacity-50"
        >
          {busyKind === 'send' ? 'Sending…' : unchanged ? 'Send' : 'Send edited'}
        </button>
      </div>
    </div>
  );
};

// Supervised draft-and-approve: shows EVERY pending AI draft for this sub-thread
// so a human can edit and Send, or Discard, each one independently. Nothing goes
// to the customer without this approval.
const SuggestedReply: React.FC<Props> = ({ threadId, refreshKey, onAfterAction, onHasContent }) => {
  const [drafts, setDrafts] = useState<AiDraft[]>([]);

  const load = useCallback(() => {
    getDrafts(threadId)
      .then((ds) => setDrafts(ds || []))
      .catch(() => { /* surfaced on next load */ });
  }, [threadId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Report content presence to the parent (for the collapsible section gating).
  useEffect(() => { onHasContent?.(drafts.length > 0); }, [drafts.length, onHasContent]);

  if (drafts.length === 0) return null;

  // Refetch after any action so the list reflects the server (a sent/discarded
  // draft drops out, and any newly generated drafts appear).
  const afterAction = () => {
    onAfterAction();
    load();
  };

  return (
    <>
      {drafts.map((d) => (
        <DraftCard key={d.id} draft={d} onAfterAction={afterAction} />
      ))}
    </>
  );
};

export default SuggestedReply;
